import React, { useState } from 'react';
import { Car, User, Upload, CheckCircle, AlertCircle } from 'lucide-react';
import logo from '../assets/WhatsApp_Image_2026-02-24_at_10.33.50_AM-removebg-preview.png'

export default function AttachYourCab() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    vehicleType: '',
    vehicleModel: '',
    vehicleNumber: '',
    licensePlate: '',
    licenseNumber: '',
    experience: '',
    images: []
  });
  
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);


  // Add this component after the header or before the form
const CommissionNote = () => (
  <div className="bg-gradient-to-r from-green-50 to-blue-50 border-l-4 border-green-500 rounded-lg p-4 mb-6">
    <h3 className="text-sm font-semibold text-gray-800 mb-2 flex items-center">
      <svg className="w-5 h-5 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
      </svg>
      Your Earning Potential with Safe Track
    </h3>
    
    <div className="grid md:grid-cols-2 gap-4 mt-3">
      {/* Local Package */}
      <div className="bg-white rounded-lg p-3 shadow-sm">
        <div className="flex items-center mb-2">
          <span className="text-lg mr-2">🚖</span>
          <span className="font-semibold text-gray-700">Local Package</span>
        </div>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Your contribution:</span>
            <span className="font-medium text-green-600">₹50 per duty</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Customer pays:</span>
            <span className="font-medium text-blue-600">₹50 per duty</span>
          </div>
          <div className="border-t border-gray-100 mt-1 pt-1">
            <div className="flex justify-between font-semibold">
              <span>Total Trip Fee:</span>
              <span className="text-green-700">₹100 per duty</span>
            </div>
          </div>
        </div>
      </div>

      {/* Outstation */}
      <div className="bg-white rounded-lg p-3 shadow-sm">
        <div className="flex items-center mb-2">
          <span className="text-lg mr-2">🏞️</span>
          <span className="font-semibold text-gray-700">Outstation</span>
        </div>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Your contribution:</span>
            <span className="font-medium text-green-600">₹100 per day</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Customer pays:</span>
            <span className="font-medium text-blue-600">₹100 per day</span>
          </div>
          <div className="border-t border-gray-100 mt-1 pt-1">
            <div className="flex justify-between font-semibold">
              <span>Total Trip Fee:</span>
              <span className="text-green-700">₹200 per day</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="mt-3 flex items-start bg-yellow-50 p-2 rounded">
      <svg className="w-4 h-4 text-yellow-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
      </svg>
      <p className="text-xs text-gray-600">
        <span className="font-semibold">Note:</span> Trip fees are collected per duty/day only on confirmed bookings. 
        This covers platform maintenance, 24/7 support.
      </p>
    </div>
  </div>
);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + imagePreviews.length > 5) {
      setErrors(prev => ({ ...prev, images: 'Maximum 5 images allowed' }));
      return;
    }
    
    const newPreviews = files.map(file => ({
      file,
      url: URL.createObjectURL(file),
      name: file.name
    }));
    
    setImagePreviews(prev => [...prev, ...newPreviews]);
    setFormData(prev => ({ ...prev, images: [...prev.images, ...files] }));
    if (errors.images) {
      setErrors(prev => ({ ...prev, images: '' }));
    }
  };

  const removeImage = (index) => {
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Invalid phone number';
    }
    if (!formData.vehicleType) newErrors.vehicleType = 'Vehicle type is required';
    if (!formData.vehicleModel.trim()) newErrors.vehicleModel = 'Vehicle model is required';
    if (!formData.vehicleNumber.trim()) newErrors.vehicleNumber = 'Vehicle number is required';
    if (!formData.licensePlate.trim()) newErrors.licensePlate = 'License plate is required';
    if (!formData.licenseNumber.trim()) newErrors.licenseNumber = 'License number is required';
    if (!formData.experience) newErrors.experience = 'Experience is required';
    if (imagePreviews.length === 0) newErrors.images = 'Please upload at least one vehicle image';
    
    return newErrors;
  };

  // Format the WhatsApp message
  const formatWhatsAppMessage = () => {
    const experienceLabels = {
      '1-2': '1-2 years',
      '3-5': '3-5 years',
      '5-10': '5-10 years',
      '10+': '10+ years'
    };

    const vehicleTypeLabels = {
      'sedan': 'Sedan',
      'suv': 'SUV',
      'hatchback': 'Hatchback',
      'luxury': 'Luxury'
    };

    const message = `
🚖 *NEW CAB ATTACHMENT REQUEST* 🚖

*📋 PERSONAL INFORMATION*
━━━━━━━━━━━━━━━━━━━━━━━
👤 *Name:* ${formData.fullName}
📧 *Email:* ${formData.email}
📱 *Phone:* ${formData.phone}
⏱️ *Experience:* ${experienceLabels[formData.experience]}

*🚗 VEHICLE DETAILS*
━━━━━━━━━━━━━━━━━━━━━━━
🚙 *Type:* ${vehicleTypeLabels[formData.vehicleType]}
🔧 *Model:* ${formData.vehicleModel}
🔢 *Registration No:* ${formData.vehicleNumber}
🪪 *License Plate:* ${formData.licensePlate}
🪪 *Driver's License:* ${formData.licenseNumber}

*📸 IMAGES UPLOADED*
━━━━━━━━━━━━━━━━━━━━━━━
📷 *Total Images:* ${imagePreviews.length}

*⏰ SUBMISSION TIME*
━━━━━━━━━━━━━━━━━━━━━━━
🕐 *Submitted:* ${new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}

*Please check the uploaded images in the attachment section below.*
    `;

    return message.trim();
  };

  // Handle images for WhatsApp (you'll need to implement this based on your backend)
  const uploadImagesAndGetUrls = async (images) => {
    // Option 1: If you have a backend that can host images and return URLs
    // This would require setting up an API endpoint to upload images
    
    // Option 2: For now, we'll just note the image count in the message
    // and you can manually share images separately
    
    // Option 3: Use a service like Cloudinary, ImgBB, etc.
    
    // For this example, we'll just return placeholder URLs
    // You'll need to implement actual image hosting based on your infrastructure
    return images.map((_, index) => `[Image ${index + 1} attached separately]`);
  };

  const handleSubmit = async () => {
    const newErrors = validate();
    
    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);
      setError('');
      
      try {
        // Format the message for WhatsApp
        const message = formatWhatsAppMessage();
        
        // Encode the message for URL
        const encodedMessage = encodeURIComponent(message);
        
        // Your WhatsApp number (replace with actual number)
        const whatsappNumber = '919150410309'; // Remove any + or spaces, use country code without +
        
        // Create WhatsApp URL
        const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
        
        // Open WhatsApp in a new tab
        window.open(whatsappUrl, '_blank');
        
        // Also log the data for debugging
        console.log('Form Data:', formData);
        console.log('Images:', imagePreviews.length, 'images uploaded');
        
        // Show success message
        setSubmitted(true);
        
        // Optional: You can also try to send images via WhatsApp Web API
        // Note: Direct image sending via URL parameters is not supported
        // You may need to use WhatsApp Business API for that
        
      } catch (error) {
        console.error('Error sending to WhatsApp:', error);
        setError('Failed to open WhatsApp. Please try again or contact support.');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setErrors(newErrors);
      // Scroll to first error
      const firstError = document.querySelector('.border-red-500');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="mb-6 flex justify-center">
            <div className="bg-green-100 rounded-full p-4">
              <CheckCircle className="w-16 h-16 text-green-600" />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">WhatsApp Opened!</h2>
          <p className="text-gray-600 text-lg mb-2">Your application has been prepared.</p>
          <p className="text-gray-500 mb-6">Please check WhatsApp to send your application with images.</p>
          
          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <p className="text-sm text-blue-800">
              📱 <strong>Note:</strong> Please send the uploaded images separately in the WhatsApp chat after the message.
            </p>
          </div>
          
          <button
            onClick={() => {
              setSubmitted(false);
              setFormData({
                fullName: '',
                email: '',
                phone: '',
                vehicleType: '',
                vehicleModel: '',
                vehicleNumber: '',
                licensePlate: '',
                licenseNumber: '',
                experience: '',
                images: []
              });
              setImagePreviews([]);
            }}
            className="bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Submit Another Application
          </button>
        </div>
      </div>
    );
  }

  return (
    <div id='attachcab' className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-100 to-blue-200 py-12 px-4">
      <div className="max-w-4xl mx-auto">
       <div className="flex items-center gap-6 mb-8">
  {/* Left side - Image */}
  <div className="flex-shrink-0">
    <img 
      src={logo} 
      alt="Safe Track Cab Services Logo" 
      className="h-24 w-auto object-contain" 
    />
  </div>
  
  {/* Right side - All text (vertically centered) */}
  <div>
    <h1 className="text-2xl font-bold mb-1" style={{ color: '#28aa96' }}>
      Join Safe Track Cab Services
    </h1>
    <p className="text-lg text-gray-700">
      Attach your cab and start earning today!
    </p>
  </div>
</div>


 <CommissionNote />

        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-10">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <p className="text-red-600">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <User className="w-6 h-6 mr-2 text-blue-600" />
                Personal Information
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      errors.fullName ? 'border-red-500' : 'border-blue-200'
                    } focus:border-blue-500 focus:outline-none transition-colors`}
                    placeholder="Enter your full name"
                  />
                  {errors.fullName && (
                    <p className="text-red-500 text-xs mt-1 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {errors.fullName}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      errors.email ? 'border-red-500' : 'border-blue-200'
                    } focus:border-blue-500 focus:outline-none transition-colors`}
                    placeholder="your.email@example.com"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      errors.phone ? 'border-red-500' : 'border-blue-200'
                    } focus:border-blue-500 focus:outline-none transition-colors`}
                    placeholder="9876543210"
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-xs mt-1 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {errors.phone}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Driving Experience *
                  </label>
                  <select
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      errors.experience ? 'border-red-500' : 'border-blue-200'
                    } focus:border-blue-500 focus:outline-none transition-colors bg-white`}
                  >
                    <option value="">Select experience</option>
                    <option value="1-2">1-2 years</option>
                    <option value="3-5">3-5 years</option>
                    <option value="5-10">5-10 years</option>
                    <option value="10+">10+ years</option>
                  </select>
                  {errors.experience && (
                    <p className="text-red-500 text-xs mt-1 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {errors.experience}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Car className="w-6 h-6 mr-2 text-blue-600" />
                Vehicle Information
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Vehicle Type *
                  </label>
                  <select
                    name="vehicleType"
                    value={formData.vehicleType}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      errors.vehicleType ? 'border-red-500' : 'border-blue-200'
                    } focus:border-blue-500 focus:outline-none transition-colors bg-white`}
                  >
                    <option value="">Select vehicle type</option>
                    <option value="sedan">Sedan</option>
                    <option value="suv">SUV</option>
                    <option value="hatchback">Hatchback</option>
                    <option value="luxury">Luxury</option>
                  </select>
                  {errors.vehicleType && (
                    <p className="text-red-500 text-xs mt-1 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {errors.vehicleType}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Vehicle Model *
                  </label>
                  <input
                    type="text"
                    name="vehicleModel"
                    value={formData.vehicleModel}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      errors.vehicleModel ? 'border-red-500' : 'border-blue-200'
                    } focus:border-blue-500 focus:outline-none transition-colors`}
                    placeholder="e.g., Honda City 2022"
                  />
                  {errors.vehicleModel && (
                    <p className="text-red-500 text-xs mt-1 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {errors.vehicleModel}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Vehicle Registration Number *
                  </label>
                  <input
                    type="text"
                    name="vehicleNumber"
                    value={formData.vehicleNumber}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      errors.vehicleNumber ? 'border-red-500' : 'border-blue-200'
                    } focus:border-blue-500 focus:outline-none transition-colors`}
                    placeholder="TN 01 AB 1234"
                  />
                  {errors.vehicleNumber && (
                    <p className="text-red-500 text-xs mt-1 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {errors.vehicleNumber}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    License Plate *
                  </label>
                  <input
                    type="text"
                    name="licensePlate"
                    value={formData.licensePlate}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      errors.licensePlate ? 'border-red-500' : 'border-blue-200'
                    } focus:border-blue-500 focus:outline-none transition-colors`}
                    placeholder="Enter license plate"
                  />
                  {errors.licensePlate && (
                    <p className="text-red-500 text-xs mt-1 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {errors.licensePlate}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Driver's License Number *
                  </label>
                  <input
                    type="text"
                    name="licenseNumber"
                    value={formData.licenseNumber}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 rounded-lg border-2 ${
                      errors.licenseNumber ? 'border-red-500' : 'border-blue-200'
                    } focus:border-blue-500 focus:outline-none transition-colors`}
                    placeholder="Enter your driver's license number"
                  />
                  {errors.licenseNumber && (
                    <p className="text-red-500 text-xs mt-1 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {errors.licenseNumber}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                <Upload className="w-6 h-6 mr-2 text-blue-600" />
                Vehicle Images *
              </h2>
              <p className="text-sm text-gray-600 mb-4">Upload clear photos of your vehicle (Maximum 5 images)</p>
              
              <div className="border-2 border-dashed border-blue-300 rounded-lg p-8 text-center hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  id="images"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <label
                  htmlFor="images"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-12 h-12 text-blue-500 mb-3" />
                  <span className="text-blue-600 font-semibold mb-1">Click to upload images</span>
                  <span className="text-sm text-gray-500">PNG, JPG up to 10MB each</span>
                </label>
              </div>

              {errors.images && (
                <p className="text-red-500 text-sm mt-2 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  {errors.images}
                </p>
              )}

              {imagePreviews.length > 0 && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
                    {imagePreviews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={preview.url}
                          alt={`Vehicle ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border-2 border-blue-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                    <p className="text-sm text-yellow-800 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-2" />
                      <strong>Note:</strong> After clicking submit, WhatsApp will open with your application details. Please send the images above separately in the WhatsApp chat.
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="pt-6">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`w-full bg-gradient-to-r from-green-600 to-green-700 text-white font-bold py-4 px-6 rounded-lg hover:from-green-700 hover:to-green-800 transform hover:scale-[1.02] transition-all shadow-lg hover:shadow-xl ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </span>
                ) : (
                  'Submit via WhatsApp'
                )}
              </button>
              <p className="text-center text-sm text-gray-500 mt-4">
                By submitting, you agree to Safe Track Cab Services terms and conditions
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
